import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

export default function Unsubscribe() {
    const [params] = useSearchParams();
    const [state, setState] = useState({ loading: true, ok: false, category: null, email: null, error: null });

    useEffect(() => {
        const token = params.get("token");
        if (!token) {
            setState({ loading: false, ok: false, error: "Missing token — please use the link from the email." });
            return;
        }
        api.get(`/notifications/unsubscribe?token=${encodeURIComponent(token)}`)
            .then((r) => setState({ loading: false, ok: true, ...r.data, error: null }))
            .catch((e) => setState({
                loading: false, ok: false,
                error: e?.response?.data?.detail || "This link is invalid or expired.",
            }));
    }, [params]);

    return (
        <div className="min-h-screen flex flex-col text-white">
            <Seo title="Unsubscribed — SRT Finder" canonicalPath="/unsubscribe" description="Manage your SRT Finder email preferences." />

            <header className="max-w-3xl w-full mx-auto px-5 py-6">
                <Logo size="sm" />
            </header>

            <main className="flex-1 flex items-center justify-center px-5">
                <div
                    className="w-full max-w-md rounded-3xl p-8"
                    style={{
                        background: "rgba(20,20,20,0.6)",
                        border: "1px solid rgba(255,255,255,0.06)",
                        backdropFilter: "blur(14px)",
                    }}
                    data-testid="unsubscribe-card"
                >
                    {state.loading && (
                        <p className="text-onyx-500 text-sm" data-testid="unsub-loading">Working on it…</p>
                    )}
                    {!state.loading && state.ok && (
                        <>
                            <CheckCircle2 className="w-8 h-8 text-hellcat mb-4" />
                            <h1 className="text-2xl mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                                You're unsubscribed
                            </h1>
                            <p className="text-onyx-500 text-sm leading-relaxed" data-testid="unsub-success">
                                We won't send any more <b className="text-white">{state.category === "all" ? "SRT Finder" : state.category}</b> emails
                                {state.email ? <> to <b className="text-white">{state.email}</b></> : null}.
                                You can turn alerts back on any time from your Garage → Notification settings.
                            </p>
                            <Link
                                to="/garage"
                                data-testid="unsub-back-to-garage"
                                className="btn-red inline-flex mt-6 px-5 py-3 text-sm font-bold"
                            >
                                Manage preferences
                            </Link>
                        </>
                    )}
                    {!state.loading && !state.ok && (
                        <>
                            <AlertTriangle className="w-8 h-8 text-hellcat mb-4" />
                            <h1 className="text-2xl mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                                Link not recognized
                            </h1>
                            <p className="text-onyx-500 text-sm leading-relaxed" data-testid="unsub-error">
                                {state.error}
                            </p>
                            <Link
                                to="/"
                                data-testid="unsub-back-home"
                                className="btn-ghost inline-flex mt-6 px-5 py-3 text-sm font-bold"
                            >
                                Back home
                            </Link>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
}
